/** Finansowanie umowy - Warstwa 1 (poziom umowy). Zgodne z backend BC `financing` (Faza A). */

export type FinancingKind = 'CASH' | 'CREDIT'

export type CreditStatus = 'APPLIED' | 'APPROVED' | 'DISBURSED' | 'REJECTED'

export const CREDIT_STATUS_LABELS: Record<CreditStatus, string> = {
  APPLIED: 'Wniosek',
  APPROVED: 'Akceptacja',
  DISBURSED: 'Uruchomiony',
  REJECTED: 'Odrzucony',
}

export type FinancingSource = {
  id: number
  kind: FinancingKind
  amount: number
  bankCode: string | null
  creditStatus: CreditStatus | null
  borrowerPartyId: number | null
  externalRef: string | null
  verifiedAt: string | null   // data weryfikacji (CREDIT)
  approvedAt: string | null   // data akceptacji (CREDIT)
  disbursedAt: string | null  // data wypłaty/uruchomienia (CREDIT)
}

export type FinancingSummary = {
  totalAmount: number
  cashAmount: number
  creditDisbursed: number // suma kredytów uruchomionych (DISBURSED) - liczy się do pokrycia
  creditPending: number   // suma APPLIED+APPROVED - informacyjnie
  coveredTotal: number    // cashAmount + creditDisbursed
  difference: number      // totalAmount - coveredTotal
  elementPriceTotal: number // suma cen produktów (ELEMENT_PRICE) - kontrola spójności z total
  paidTotal: number       // suma wpłat gotówkowych
  refund: number          // nadpłata do zwrotu (wpłaty - harmonogram, gdy > 0)
  cashRemaining: number   // gotówka pozostała do wpłaty (harmonogram - zaalokowane)
}

export type ContractElementPrice = {
  contractElementId: number
  price: number | null
}

export type Financing = {
  sources: FinancingSource[]
  elementPrices: ContractElementPrice[]
  summary: FinancingSummary
}

export type Bank = {
  code: string
  labelPl: string
  sortOrder: number
  isActive: boolean
}

/** Dodanie źródła finansowania (kind wymagany). bank/status/daty tylko dla CREDIT. */
export type AddSourceInput = {
  kind: FinancingKind
  amount: number
  bankCode?: string | null
  creditStatus?: CreditStatus | null
  borrowerPartyId?: number | null
  externalRef?: string | null
  verifiedAt?: string | null
  approvedAt?: string | null
  disbursedAt?: string | null
}

/** Edycja źródła (kind niezmienny). */
export type UpdateSourceInput = {
  amount: number
  bankCode?: string | null
  creditStatus?: CreditStatus | null
  borrowerPartyId?: number | null
  externalRef?: string | null
  verifiedAt?: string | null
  approvedAt?: string | null
  disbursedAt?: string | null
}

// --- Harmonogram gotówki (Faza B) ---

export type ScheduleBasis = 'SIGNING' | 'DELIVERY' | 'INSTALLATION' | 'FIXED'

export const SCHEDULE_BASIS_LABELS: Record<ScheduleBasis, string> = {
  SIGNING: 'Od podpisania',
  DELIVERY: 'Od dostawy',
  INSTALLATION: 'Od montażu',
  FIXED: 'Data stała',
}

// --- Szablon harmonogramu (per konfiguracja, admin) ---

export type CashScheduleTemplateItem = {
  id?: number
  seq: number
  label: string
  percent: number
  basis: ScheduleBasis
  offsetDays: number | null
}

export type CashScheduleTemplate = {
  configurationCode: string
  label: string | null
  isActive: boolean
  items: CashScheduleTemplateItem[]
}

export type SaveCashScheduleTemplateInput = {
  label: string
  isActive: boolean
  items: { seq: number; label: string; percent: number; basis: ScheduleBasis; offsetDays: number | null }[]
}

export type CashScheduleItem = {
  id: number
  contractElementId: number
  seq: number
  label: string | null
  basis: ScheduleBasis
  offsetDays: number | null
  percent: number | null
  amount: number
  dueDate: string | null
  paidAt: string | null
  paidAmount: number | null
}

export type CashPaymentAllocationRow = {
  id: number
  trancheId: number
  amount: number
}

export type CashPayment = {
  id: number
  paidAt: string
  amount: number
  label: string | null
  allocatedAmount: number   // suma alokacji tej wplaty
  unallocatedAmount: number // amount - allocated (do alokacji)
  allocations: CashPaymentAllocationRow[]
}

/** Informacyjny podglad: jak gotowka umowy rozejdzie sie na produkty (wg ELEMENT_PRICE). */
export type CashSplitItem = {
  contractElementId: number
  price: number | null
  allocatedCash: number
}

export type CashSplit = {
  byPrice: boolean // false = brak cen, podzial rowny (ostrzezenie)
  items: CashSplitItem[]
}

export type AllocationMode = 'AUTO' | 'MANUAL'

export type PaymentAllocation = { trancheId: number; amount: number }

export type RegisterPaymentInput = {
  paidAt: string
  amount: number
  label?: string | null
  mode: AllocationMode
  allocations?: PaymentAllocation[]
}

/** Realokacja niezaalokowanej reszty istniejacej wplaty. */
export type AllocateInput = {
  mode: AllocationMode
  allocations?: PaymentAllocation[]
}

/** Ewidencja zwrotu nadpłaty (amount > 0 - kwota zwrócona klientowi). */
export type RefundInput = {
  amount: number
  refundedAt: string
  label?: string | null
}
